<script>
  import { createEventDispatcher, onMount } from 'svelte';
  import { writable, get } from 'svelte/store';
  import { selectedStudy } from '$stores/selectionStore';
  import { theme } from '$stores/themeStore';
  import { userRole, isReadOnly } from '$stores/sessionStore';

  const dispatch = createEventDispatcher();
  export let showForm = false;
  export let editSchedule = null;
  export let scheduleStatus = null;

  let scheduleData = writable({
    _id: null,
    studyGUID: $selectedStudy?._id,
    name: '',
    description: '',
    active: true,
    status: 'Draft',
    protocolVersion: '1',
    scheduleVersion: 1,
  });

  const statusOptions = [
    { value: 'Draft', label: 'Draft' },
    { value: 'Published', label: 'Published' },
    { value: 'Archived', label: 'Archived' },
  ];

  $: if (editSchedule) {
    const formattedSchedule = { ...editSchedule };
    scheduleData.set(formattedSchedule);
  }

  $: if (showForm && !editSchedule) {
    resetScheduleData();
  }

  function resetScheduleData() {
    scheduleData.set({
      _id: null,
      studyGUID: $selectedStudy?._id,
      name: '',
      description: '',
      active: true,
      status: 'Draft',
      protocolVersion: '1',
      scheduleVersion: 1,
    });
  }

  function close() {
    dispatch('close');
  }

  async function handleSubmit() {
    const schedule = get(scheduleData);
    dispatch('submit', schedule);
    close();
  }

  // Determine if save button should be disabled - only for Published/Archived schedules
  $: saveButtonDisabled = isReadOnly() || (editSchedule && (scheduleStatus === 'Published' || scheduleStatus === 'Archived'));
  
  // Determine if form fields should be disabled - only for Published/Archived schedules
  $: formFieldsDisabled = editSchedule && (scheduleStatus === 'Published' || scheduleStatus === 'Archived');
</script>

{#if showForm}
  <div class="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm">
    <!-- svelte-ignore a11y-click-events-have-key-events -->
    <!-- svelte-ignore a11y-no-static-element-interactions -->
    <div class="modal-overlay absolute inset-0 bg-slate-800 opacity-75" on:click={close}></div>
    <!-- svelte-ignore a11y-click-events-have-key-events -->
    <!-- svelte-ignore a11y-no-static-element-interactions -->
    <div
      class="bg-white dark:bg-gray-900 p-4 rounded-lg shadow-lg w-full max-w-2xl relative z-60 overflow-y-auto max-h-[90vh]"
      on:click|stopPropagation
    >
      <header class="flex justify-between items-start mb-4">
        <h2 class="text-xl font-bold text-gray-900 dark:text-gray-100">
          {editSchedule ? 'Edit Schedule' : 'New Schedule'}
        </h2>
        <button
          on:click={close}
          class="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
        >
          &times;
        </button>
      </header>
      <form on:submit|preventDefault={handleSubmit} class="space-y-4">
        <div class="flex gap-3">
          <div class="w-full">
            <label for="name" class="block text-xs pb-1 text-gray-700 dark:text-gray-200"
              >Name</label
            >
            <input
              type="text"
              id="name"
              bind:value={$scheduleData.name}
              disabled={formFieldsDisabled}
              class="border p-1 w-full h-8 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
              class:opacity-75={formFieldsDisabled}
              class:cursor-not-allowed={formFieldsDisabled}
              required
            />
          </div>
        </div>

        <div class="flex gap-3">
          <div class="w-1/2">
            <label for="protocolVersion" class="block text-xs pb-1 text-gray-700 dark:text-gray-200"
              >Protocol Version</label
            >
            <input
              type="text"
              id="protocolVersion"
              bind:value={$scheduleData.protocolVersion}
              disabled={formFieldsDisabled}
              class="border p-1 w-full h-8 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
              class:opacity-75={formFieldsDisabled}
              class:cursor-not-allowed={formFieldsDisabled}
              required
            />
          </div>
          <div class="w-1/2">
            <label for="scheduleVersion" class="block text-xs pb-1 text-gray-700 dark:text-gray-200"
              >Schedule Version</label
            >
            <input
              type="number"
              id="scheduleVersion"
              bind:value={$scheduleData.scheduleVersion}
              class="border p-1 w-full h-8 rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 bg-gray-100"
              readonly
            />
          </div>
        </div>

        <div class="flex gap-3">
          <div class="w-1/2">
            <label for="status" class="block text-xs pb-1 text-gray-700 dark:text-gray-200"
              >Status</label
            >
            <select
              id="status"
              bind:value={$scheduleData.status}
              disabled={formFieldsDisabled}
              class="form-select h-8 w-full border px-2 p-0 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white appearance-none"
              class:opacity-75={formFieldsDisabled}
              class:cursor-not-allowed={formFieldsDisabled}
            >
              {#each statusOptions as option}
                <option value={option.value}>{option.label}</option>
              {/each}
            </select>
          </div>
          <div class="w-1/2">
            <label for="active" class="block text-xs pb-1 text-gray-700 dark:text-gray-200"
              >Active</label
            >
            <select
              id="active"
              bind:value={$scheduleData.active}
              disabled={formFieldsDisabled}
              class="form-select h-8 w-full border px-2 p-0 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white appearance-none"
              class:opacity-75={formFieldsDisabled}
              class:cursor-not-allowed={formFieldsDisabled}
            >
              <option value={true}>Yes</option>
              <option value={false}>No</option>
            </select>
          </div>
        </div>

        <div class="flex gap-3">
          <div class="w-full">
            <label for="description" class="block text-xs pb-1 text-gray-700 dark:text-gray-200"
              >Description</label
            >
            <textarea
              id="description"
              bind:value={$scheduleData.description}
              disabled={formFieldsDisabled}
              class="border p-1 w-full rounded dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100"
              class:opacity-75={formFieldsDisabled}
              class:cursor-not-allowed={formFieldsDisabled}
              rows="3"
            ></textarea>
          </div>
        </div>

        <div class="flex justify-end space-x-2 pt-10">
          <button
            type="button"
            on:click={close}
            class="bg-gray-500 text-white py-1 px-2 rounded hover:bg-gray-700">Cancel</button
          >
          <button 
            type="submit" 
            class="bg-blue-500 text-white py-1 px-2 rounded hover:bg-blue-700"
            disabled={saveButtonDisabled}
            class:opacity-50={saveButtonDisabled}
            class:cursor-not-allowed={saveButtonDisabled}
            title={formFieldsDisabled ? 'Published or Archived schedules cannot be modified' : 'Save changes'}
          >Save</button>
        </div>
      </form>
    </div>
  </div>
{/if}
